import React, { createContext, useContext, useEffect, useState } from "react";
import Axios from "../axios";
import { staticNavData } from "../navigation/sidenav/sidenavData";
import { isBrowser } from "../utils/runtime";

const AUTH_EXPIRED_EVENT = "sevenly:auth-expired";
const AuthContext = createContext();

function normalizeKey(value) {
  return String(value || "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toLowerCase();
}

function normalizeRole(roleValue) {
  if (!roleValue) {
    return "";
  }
  if (typeof roleValue === "string") {
    return roleValue;
  }
  return roleValue.role || "";
}


const NAV_PERMISSION_ALIASES = {
  [normalizeKey("Ship Tool")]: ["Ship Tool", "Shop Tool", "Hotel Search", "Market"],
  [normalizeKey("Shop Tool")]: ["Shop Tool", "Ship Tool", "Hotel Search", "Market"],
  [normalizeKey("Hotel Search")]: ["Hotel Search", "Shop Tool", "Ship Tool", "Market"],
  [normalizeKey("Featured Deals")]: ["Featured Deals", "Featured Hotels"],
  [normalizeKey("Hotel Deals")]: ["Hotel Deals", "Featured Deals", "Featured Hotels"],
  [normalizeKey("Deals")]: ["Deals", "Featured Deals", "Featured Hotels"],
  [normalizeKey("Race Cations")]: ["Race Cations", "Race Management"],
};

function findPermissionForNavItem(rolePermission, navText) {
  const aliases = NAV_PERMISSION_ALIASES[normalizeKey(navText)] || [navText];
  for (const alias of aliases) {
    const permission = rolePermission.find(
      (perm) => normalizeKey(perm.page) === normalizeKey(alias)
    );
    if (permission) {
      return permission;
    }
  }
  return null;
}

function resolveRolePermissions(roles, roleValue) {
  if (!Array.isArray(roles) || !roleValue) {
    return null;
  }

  const matchingRole = roles.find(
    (roleItem) => normalizeKey(normalizeRole(roleItem?.role)) === normalizeKey(roleValue)
  );

  if (!matchingRole || !Array.isArray(matchingRole.permissions)) {
    return null;
  }

  return matchingRole.permissions;
}

function permissionsChanged(currentPermissions, nextPermissions) {
  const current = Array.isArray(currentPermissions) ? currentPermissions : [];
  const next = Array.isArray(nextPermissions) ? nextPermissions : [];

  if (current.length !== next.length) {
    return true;
  }

  return current.some((permission, index) => {
    const other = next[index] || {};
    return (
      normalizeKey(permission?.page) !== normalizeKey(other?.page) ||
      Boolean(permission?.read) !== Boolean(other?.read) ||
      Boolean(permission?.write) !== Boolean(other?.write)
    );
  });
}

function normalizeUser(userValue) {
  const rolePermission = Array.isArray(userValue?.rolePermission)
    ? userValue.rolePermission
    : [];

  return {
    userid: userValue?.userid || userValue?._id,
    name: userValue?.name || "",
    email: userValue?.email || "",
    role: normalizeRole(userValue?.role),
    rolePermission,
  };
}

function isSuperAdminRole(roleValue) {
  return normalizeKey(normalizeRole(roleValue)) === normalizeKey("superadmin");
}

// Superadmin's permissions are otherwise honored from Role Management like any
// other role. Role Management itself always stays open for superadmin so it can
// never lock itself out of the one screen that could undo a mistake.
const ALWAYS_ACCESSIBLE_FOR_SUPERADMIN = ["Role Management"];

function resolveNavItem(navItem, rolePermission, isSuperAdmin) {
  const isForced =
    isSuperAdmin &&
    ALWAYS_ACCESSIBLE_FOR_SUPERADMIN.some(
      (text) => normalizeKey(text) === normalizeKey(navItem.text)
    );
  const permission = findPermissionForNavItem(rolePermission, navItem.text);
  const read = isForced || Boolean(permission?.read);
  const write = isForced || Boolean(permission?.write);

  const children = Array.isArray(navItem.children)
    ? navItem.children
      .map((child) => resolveNavItem(child, rolePermission, isSuperAdmin))
      .filter(Boolean)
    : undefined;

  if (!read && !write && !(children && children.length)) {
    return null;
  }

  return {
    ...navItem,
    read,
    write,
    ...(children ? { children } : {}),
  };
}

function buildNavData(rolePermission = [], roleValue = "") {
  const isSuperAdmin = isSuperAdminRole(roleValue);

  return staticNavData
    .map((navItem) => resolveNavItem(navItem, rolePermission, isSuperAdmin))
    .filter(Boolean);
}

function toInitialState(initialAuth) {
  if (!initialAuth || !initialAuth.authenticated || !initialAuth.user) {
    return {
      authReady: Boolean(initialAuth),
      isAuthenticated: false,
      role: "",
      user: null,
      navData: [],
    };
  }

  const user = normalizeUser(initialAuth.user);
  return {
    authReady: true,
    isAuthenticated: true,
    role: user.role,
    user,
    navData: buildNavData(user.rolePermission, user.role),
  };
}

export function AuthProvider({ children, initialAuth = null }) {
  const initialState = toInitialState(initialAuth);

  const [user, setUser] = useState(initialState.user);
  const [role, setRole] = useState(initialState.role);
  const [navData, setNavData] = useState(initialState.navData);
  const [isAuthenticated, setIsAuthenticated] = useState(
    initialState.isAuthenticated
  );
  const [authReady, setAuthReady] = useState(initialState.authReady);
  const [openSideNav, setOpenSideNav] = useState(true);

  const clearAuthState = () => {
    setUser(null);
    setRole("");
    setNavData([]);
    setIsAuthenticated(false);
    setAuthReady(true);
  };

  const applySession = (sessionPayload) => {
    if (sessionPayload?.authenticated && sessionPayload.user) {
      const normalizedUser = normalizeUser(sessionPayload.user);
      setUser(normalizedUser);
      setRole(normalizedUser.role);
      setNavData(buildNavData(normalizedUser.rolePermission, normalizedUser.role));
      setIsAuthenticated(true);
      setAuthReady(true);
      return;
    }

    clearAuthState();
  };

  const refreshSession = async () => {
    try {
      const response = await Axios.get(
        `${process.env.REACT_APP_BACKEND}auth/session`
      );
      applySession(response.data);
      return true;
    } catch (error) {
      clearAuthState();
      return false;
    }
  };

  useEffect(() => {
    if (!authReady) {
      refreshSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady]);

  useEffect(() => {
    if (!isBrowser || !authReady || !isAuthenticated || !role) {
      return undefined;
    }

    let cancelled = false;

    const syncRolePermissions = async () => {
      try {
        const response = await Axios.post(
          `${process.env.REACT_APP_BACKEND}get-roles`,
          {
            page: 1,
            searchChar: "",
          }
        );

        if (cancelled) {
          return;
        }

        const roles = Array.isArray(response?.data?.data) ? response.data.data : [];
        const nextPermissions = resolveRolePermissions(roles, role);

        if (!nextPermissions) {
          return;
        }

        if (!permissionsChanged(user?.rolePermission, nextPermissions)) {
          return;
        }

        setUser((currentUser) =>
          currentUser
            ? { ...currentUser, rolePermission: nextPermissions }
            : currentUser
        );
        setNavData(buildNavData(nextPermissions, role));
      } catch (error) {
        // Fall back to the permissions already present in the session payload.
      }
    };

    syncRolePermissions();

    return () => {
      cancelled = true;
    };
  }, [authReady, isAuthenticated, role, user]);

  useEffect(() => {
    if (!isBrowser) {
      return undefined;
    }

    const handleAuthExpired = () => {
      clearAuthState();
    };

    window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    };
  }, []);

  const login = async (userObj) => {
    applySession({ authenticated: true, user: userObj });
    await refreshSession();
  };

  const signup = (userData) => {
    setUser(userData);
  };

  const logout = async () => {
    try {
      await Axios.post(`${process.env.REACT_APP_BACKEND}auth/logout`);
    } catch (error) {
      // no-op: local state still needs to be cleared
    }
    clearAuthState();
  };

  const SideNav = (val) => {
    setOpenSideNav(val);
  };

  return (
    <AuthContext.Provider
      value={{
        authReady,
        isAuthenticated,
        navData,
        openSideNav,
        role,
        user,
        login,
        logout,
        refreshSession,
        SideNav,
        signup,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
